import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Heart, AlertCircle, ArrowUpRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import SectionLabel from "@/components/SectionLabel";

export default function SubscriptionConfirmed() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({
    loading: true,
    error: null,
    email: null,
    already_confirmed: false,
  });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, error: "Missing confirmation token.", email: null, already_confirmed: false });
      return;
    }
    api
      .get(`/public/confirm-subscription?token=${encodeURIComponent(token)}`)
      .then((r) =>
        setState({
          loading: false,
          error: null,
          email: r.data.email,
          already_confirmed: !!r.data.already_confirmed,
        })
      )
      .catch((err) =>
        setState({
          loading: false,
          error:
            formatApiError(err?.response?.data?.detail) ||
            "We couldn't confirm that link. It may have expired — try subscribing again.",
          email: null,
          already_confirmed: false,
        })
      );
  }, [token]);

  return (
    <div
      className="min-h-[80vh] flex items-center justify-center px-6 py-20"
      data-testid="subscription-confirmed-page"
    >
      <div className="max-w-2xl w-full space-y-8">
        <SectionLabel label="Inner Circle · Newsletter" />

        {state.loading && (
          <div
            className="surface p-10 text-center"
            data-testid="subscription-confirming"
          >
            <p className="font-serif italic text-white/55">Confirming your subscription…</p>
          </div>
        )}

        {!state.loading && state.error && (
          <div
            className="surface p-10 lg:p-14 text-center space-y-5"
            data-testid="subscription-confirmed-error"
          >
            <AlertCircle
              size={42}
              strokeWidth={1.2}
              style={{ color: "var(--gold)" }}
              className="mx-auto"
            />
            <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light">
              Something didn't{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                land.
              </em>
            </h1>
            <p className="text-white/65 leading-relaxed max-w-md mx-auto">{state.error}</p>
            <Link
              to="/"
              className="btn-ghost-gold inline-flex items-center gap-2 px-6 py-3 text-xs"
              data-testid="confirm-error-home"
            >
              Back to Home
              <ArrowUpRight size={14} />
            </Link>
          </div>
        )}

        {!state.loading && !state.error && (
          <div
            className="surface p-10 lg:p-14 text-center space-y-6"
            data-testid="subscription-confirmed-success"
          >
            <div className="relative inline-flex items-center justify-center mx-auto">
              <div
                className="absolute -inset-3 rounded-full opacity-40 blur-2xl"
                style={{ background: "var(--gold)" }}
              />
              <div
                className="relative w-20 h-20 rounded-full border-2 flex items-center justify-center"
                style={{ borderColor: "rgba(232,180,184,0.55)" }}
              >
                {state.already_confirmed ? (
                  <Heart
                    size={32}
                    strokeWidth={1.2}
                    style={{ color: "var(--gold)" }}
                    fill="var(--gold)"
                    fillOpacity={0.18}
                  />
                ) : (
                  <Check size={32} strokeWidth={2} style={{ color: "var(--gold)" }} />
                )}
              </div>
            </div>

            <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter font-light leading-[1]">
              {state.already_confirmed ? (
                <>
                  You're already{" "}
                  <em className="italic" style={{ color: "var(--gold)" }}>
                    locked in.
                  </em>
                </>
              ) : (
                <>
                  Welcome to the{" "}
                  <em className="italic" style={{ color: "var(--gold)" }}>
                    Inner Circle.
                  </em>
                </>
              )}
            </h1>

            {state.email && (
              <p className="text-white/55 text-sm uppercase tracking-[0.25em]">
                {state.email}
              </p>
            )}

            <p className="text-white/75 max-w-md mx-auto leading-relaxed">
              {state.already_confirmed
                ? "Your subscription is already active. No further action needed — next drop lands in your inbox."
                : "First word on every release. Early access to OVA 40 deluxe edits & the upcoming Audio Porn drop. The weekly Wellness Blog. No spam, just the work."}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
              <Link
                to="/music"
                className="btn-gold inline-flex items-center gap-2 px-6 py-3 text-xs"
                data-testid="confirm-explore-music"
              >
                Explore OVA 40
                <ArrowUpRight size={14} />
              </Link>
              <Link
                to="/inspire"
                className="btn-ghost-gold inline-flex items-center gap-2 px-6 py-3 text-xs"
                data-testid="confirm-explore-inspire"
              >
                This Week's Drop
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
